import type { FiltersContext } from '@openx/components-filters/lib/context/types.ts';
import {
  FILTER_TYPES,
  type FormatAndDevicesConfigurableFilter,
} from '@openx/components-filters/lib/FiltersBar/types.ts';
import type { TFunction } from 'i18next';
import { DealPromptFormatAndDevicesFilter } from '../../parts/DealPromptFormatAndDevicesFilter.tsx';
import {
  type DealPromptContextType,
  type DealPromptFilters,
  dealPromptContextTypes,
  dealPromptFilterFormats,
  dealPromptFilterGender,
  dealPromptFilterGoals,
  dealPromptFilterPlacement,
  dealPromptFilterPlatforms,
} from '../types.ts';
import filterCategories from './categories.json';
import { getCountriesOptions } from './getCountriesOptions.ts';

type DealPromptFiltersConfig = FiltersContext<DealPromptFilters>['filtersConfig'];

type FiltersConfigParams = {
  t: TFunction;
  contextType?: DealPromptContextType;
  featureFlagVisible?: boolean;
};

export const getStaticFiltersConfig = ({
  t,
  contextType,
  featureFlagVisible,
}: FiltersConfigParams): DealPromptFiltersConfig => {
  const FORMAT_LABELS: Record<string, string> = {
    [dealPromptFilterFormats.BANNER]: t('Banner'),
    [dealPromptFilterFormats.VIDEO]: t('Video'),
    [dealPromptFilterFormats.NATIVE]: t('Native'),
  };

  const formatAndDevicesFilter: FormatAndDevicesConfigurableFilter<'format_and_devices'> = {
    key: 'format_and_devices',
    name: t('Format & Devices'),
    type: FILTER_TYPES.FORMAT_AND_DEVICES,
    config: {
      managedKeys: ['format', 'ds_device'],
      renderContent: ({ appliedFilters, onOptionChange, isDisabled }) => (
        <DealPromptFormatAndDevicesFilter
          appliedFilters={appliedFilters as DealPromptFilters}
          isDisabled={isDisabled}
          onOptionChange={(key, value) => onOptionChange(key, value)}
        />
      ),
      getButtonValue: filters => {
        const format = (filters?.format as string[] | undefined) ?? [];
        const devices = (filters?.ds_device as string[] | undefined) ?? [];

        const hasFormat = format.length > 0;
        const hasDevices = devices.length > 0;

        if (!hasFormat && !hasDevices) return '';

        const formatLabel = hasFormat ? (FORMAT_LABELS[format[0]] ?? format[0]) : '';

        if (hasFormat && hasDevices) return `${formatLabel} • ${devices.length}`;
        if (hasFormat) return formatLabel;
        return `${devices.length}`;
      },
    },
  };

  const defaultFiltersConfig: DealPromptFiltersConfig = [
    formatAndDevicesFilter,
    {
      key: 'geography',
      name: t('Geography'),
      type: FILTER_TYPES.AUTOCOMPLETE,
      options: getCountriesOptions(),
    },
    ...(featureFlagVisible
      ? ([
          {
            key: 'goal',
            name: t('Goal'),
            type: FILTER_TYPES.RADIO,
            options: [
              { id: dealPromptFilterGoals.SPEND, name: t('Spend') },
              { id: dealPromptFilterGoals.IMPRESSIONS, name: t('Impressions') },
              { id: dealPromptFilterGoals.CTR, name: t('CTR') },
            ],
          },
          {
            key: 'ds_impressions',
            name: t('Impressions'),
            subName: t('Monthly'),
            type: FILTER_TYPES.NUMERIC_INPUT,
            config: {
              inputType: 'integer',
              fieldPlaceholder: t('Enter Min value'),
            },
          },
          {
            key: 'ds_viewability',
            name: t('Viewability'),
            subName: t('Monthly'),
            type: FILTER_TYPES.DROPDOWN,
            options: [
              { name: t('10% or greater'), id: '0.10' },
              { name: t('20% or greater'), id: '0.20' },
              { name: t('30% or greater'), id: '0.30' },
              { name: t('40% or greater'), id: '0.40' },
              { name: t('50% or greater'), id: '0.50' },
              { name: t('55% or greater'), id: '0.55' },
              { name: t('60% or greater'), id: '0.60' },
              { name: t('65% or greater'), id: '0.65' },
              { name: t('70% or greater'), id: '0.70' },
              { name: t('75% or greater'), id: '0.75' },
              { name: t('80% or greater'), id: '0.80' },
              { name: t('85% or greater'), id: '0.85' },
              { name: t('90% or greater'), id: '0.90' },
              { name: t('95% or greater'), id: '0.95' },
            ],
            config: {
              defaultValueLabel: t('--'),
              emptyPlaceholder: t('[ Select Viewability ]'),
              dropdownHeight: '220px',
            },
          },
          {
            key: 'ds_content_vertical',
            name: t('Content Vertical'),
            type: FILTER_TYPES.AUTOCOMPLETE,
            /*
            TODO: https://openxtechinc.atlassian.net/browse/EXCH-14314
            In the future dev iterations these categories should be taken from filterOptions response,
            UX must be improved too
            */
            options: filterCategories.map(iabCategory => ({
              id: iabCategory,
              name: iabCategory,
            })),
            config: {
              dropdownHeight: '220px',
            },
          },
          {
            key: 'ds_video_placement',
            name: t('Video Placement'),
            type: FILTER_TYPES.AUTOCOMPLETE,
            options: [
              { id: dealPromptFilterPlacement.INSTREAM, name: t('Instream') },
              { id: dealPromptFilterPlacement.ACCOMPANYING_CONTENT, name: t('Accompanying Content') },
              { id: dealPromptFilterPlacement.INTERSTITIAL, name: t('Interstitial') },
              { id: dealPromptFilterPlacement.NO_CONTENT_STANDALONE, name: t('No Content / Standalone') },
            ],
          },
          {
            key: 'ds_cpm',
            name: t('CPM'),
            type: FILTER_TYPES.NUMERIC_INPUT,
            config: {
              inputType: 'float',
              precision: 2,
              startAdornment: '$',
              fieldPlaceholder: t('Enter a CPM threshold'),
            },
          },
          {
            key: 'ds_is_direct',
            name: t('Inventory Quality Filter'),
            type: FILTER_TYPES.CHECKBOX,
            options: [
              {
                id: 'true',
                name: t('Exclude Indirect Inventory'),
              },
            ],
          },
        ] as DealPromptFiltersConfig)
      : []),
  ];

  if (contextType === dealPromptContextTypes.APPS) {
    return [
      ...defaultFiltersConfig,
      ...(featureFlagVisible
        ? ([
            {
              key: 'platform',
              name: t('Platform'),
              type: FILTER_TYPES.CHECKBOX,
              options: [
                { id: dealPromptFilterPlatforms.IOS, name: t('ios') },
                { id: dealPromptFilterPlatforms.ANDROID, name: t('android') },
              ],
            },
          ] as DealPromptFiltersConfig)
        : []),
    ];
  }

  if (contextType === dealPromptContextTypes.AUDIENCE) {
    return [
      {
        key: 'demographic',
        name: t('Demographic'),
        type: FILTER_TYPES.RANGE_WITH_RADIO,
        options: [
          { id: 'age', name: t('Age') },
          { id: 'gender', name: t('Gender') },
        ],
        config: {
          rangeFilterKey: 'age',
          radioFilterKey: 'gender',
          inputType: 'integer',
          precision: 0,
          defaultOption: dealPromptFilterGender.ANY,
          availableOptions: [
            { id: dealPromptFilterGender.MALE, name: t('Male') },
            { id: dealPromptFilterGender.FEMALE, name: t('Female') },
          ],
          rangeLabel: t('Age'),
          radioLabel: t('Gender'),
          detailedOptionsConfig: {
            minFieldPlaceholder: t('Enter Min value'),
            maxFieldPlaceholder: t('Enter Max value'),
            minValue: 19,
            maxValue: 99,
          },
        },
      },
      {
        key: 'uniqueUsersReach',
        name: t('Goal'),
        subName: t('Monthly'),
        type: FILTER_TYPES.NUMERIC_INPUT,
        config: {
          inputType: 'float',
          endAdornment: 'M',
          precision: 3,
          label: t('Unique Users Reach'),
          fieldPlaceholder: t('e.g., 100'),
        },
      },
    ];
  }

  return defaultFiltersConfig;
};
